import analytics from '../../analytics';

/**
 * Set loading state, if loading set loader
 * visible and fade search container
 *
 * @param {boolean} value - True if loading, otherwise false
 */
export function toggleSearchActive(value) {
  document.querySelectorAll('.car-search-main, .car-search-grid, .hedin-search-watcher-found-cars').forEach(e => {
    e.classList.toggle('search-is-active', value);
  });
}

/**
   * Format filters array for AJAX search
   * @param {Array} filters Array of filter objects
   * @param {string} currentBrand Current brand slug
   * @returns {Object} Returns filters in AJAX body format
   */
export function formatAjaxBody(filters = [], currentBrand) {
  const ajaxBody = {};

  filters.forEach((filter) => {
    let filterValue = filter.value;
    if (filter.name.match(/\.*\[\]$/, 'gi')) {
      filterValue = [filter.value];
    }

    if (ajaxBody[filter.name]) {
      ajaxBody[filter.name] = [].concat(ajaxBody[filter.name], filterValue);
    } else {
      ajaxBody[filter.name] = filterValue;
    }
  });

  // Append forceBrand parameter
  ajaxBody['forceBrand'] = currentBrand;

  // Define unnecessary search parameters that are not
  // nice to show in URL and affects only in AJAX Get parameters
  const unnecessarySearchParametersToShow = ['page', 'forceBrand'];
  const filteredAjaxBody = {};
  for (const [key, value] of Object.entries(ajaxBody)) {
    if (!unnecessarySearchParametersToShow.includes(key)) {
      filteredAjaxBody[key] = value;
    }
  }

  return {
    ajaxBody,
    filteredAjaxBody
  };
}


/**
 * Convert selected parameters to URL Search parameters
 *
 * @param {object} selectedParams Selected parameters to convert URL search params
 * @param {boolean} ignorePaging Pass true to skip including page attribute to query parameters
 * @param {string} currentPage current page
 * @returns {string}
 * @private
 */
export function convertUrlSearchParams(selectedParams = {}, ignorePaging = false, currentPage) {
  const urlParams = new URLSearchParams();

  // Insert page to parameters if not ignored
  if (!ignorePaging) {
    selectedParams.page = currentPage;
  }

  for (const [key, value] of Object.entries(selectedParams)) {
    if (Array.isArray(value)) {
      value.forEach(v => urlParams.append(key, v));
    } else {
      urlParams.append(key, value);
    }
  }

  return urlParams.toString();
}

/**
 * Pushes all selected filters to dataLayer
 *
 * @param {string} elementName - Element name inside interaction object
 * @param {object} selectedFilters - Selected filters
 */
export function pushFiltersToDataLayer(elementName, selectedFilters) {
  if (typeof elementName !== 'string') {
    console.error(`Could not push filters to dataLayer, elementName was not string, elementName was ${typeof elementName}`)
  } else {
    analytics.pushToDataLayer({
      event: 'UI Interaction',
      interaction: {
        elementName,
        type: 'click',
        selectedFilters: formatSelectedFiltersToDataLayer(selectedFilters)
      }
    })
  }
}

/**
   * Format selected filters to dataLayer friendly
   *
   * @param {object} selectedFilters - Selected filters original, from this.getSelectedFilters()
   * @returns {object} - Formated object
   *
   * @see https://crasman.atlassian.net/browse/LAAKJK-1780
   * @private
   */
export function formatSelectedFiltersToDataLayer(selectedFilters = {}) {
  if(typeof selectedFilters === 'object' && selectedFilters !== null && Object.keys(selectedFilters).length) {
    const returnValue  = {};

    for(const fieldData of Object.values(selectedFilters)) {
      // Remove square brackets from field name
      const keyName = fieldData.name.replace(/\[\]/g, '');

      // Push values in array to dataLayer
      if(!returnValue[keyName]) {
        returnValue[keyName] = [];
      }
      returnValue[keyName].push(fieldData.value);
    }

    return returnValue;
  }

  return {};
}

/**
 * Detect if element precisely center of screen
 *
 * @param {HTMLElement} element element
 *
 * @returns {boolean}
 * @private
 */
export function elementIsCenterOfScreen(element) {
  const vHeight = (window.innerHeight || document.documentElement.clientHeight);
  return (element.getBoundingClientRect().top + (element.scrollHeight / 2)) <= (vHeight / 2);
}

/**
 * Test is a filter element checkbox or radio input
 *
 * @param {HTMLElement} filter filter element
 * @returns {boolean} true if element is checkbox or radio element
 */
export function isCheckboxOrRadioFilter(filter) {
  return filter.tagName === 'INPUT' &&
         (filter.type === 'checkbox' || filter.type === 'radio') &&
         filter.checked;
}

/**
 * Test is filter a text input
 *
 * @param {HTMLElement} filter filter element
 * @returns {boolean} Return true if element is a text input, otherwise false.
 */
export function isTextInput(filter) {
  return filter.tagName === 'INPUT' && filter.type === 'text' && filter.value !== '';
}

/**
 * Test is filter a select element
 *
 * @param {HTMLElement} filter filter element
 * @returns {boolean} Return true if element is a select element, otherwise false.
 */
export function isSelectElement(filter) {
  return filter.tagName === 'SELECT' && window.multiselects[filter?.id]?.getValue()?.filter(Boolean)?.length;
}

/**
 * Convert checkbox and radio filter to simple object
 *
 * @param {HTMLElement} filter filter element
 * @returns {object} return value
 */
export function handleCheckboxAndRadio(filter) {
  const returnValue = {};
  returnValue.name = filter.name;
  returnValue.text = `
    ${filter.dataset.prefix ? filter.dataset.prefix + ' ' : ''}
    ${filter.dataset.shownValue || filter.value}
    ${filter.dataset.suffix ? filter.dataset.suffix + ' ' : ''}`.trim();
  returnValue.value = filter.value;
  return returnValue;
}

/**
 * Convert text input filter to simple object
 *
 * @param {HTMLElement} filter filter element
 * @returns {object} return value
 */
export function handleTextInput(filter) {
  const returnValue = {};
  returnValue.name = filter.name;
  returnValue.text = `
    ${filter.dataset.prefix ? filter.dataset.prefix + ' ' : ''}
    ${filter.dataset.shownValue || filter.value}
    ${filter.dataset.suffix ? filter.dataset.suffix + ' ' : ''}`.trim();
  returnValue.value = filter.value;
  return returnValue;
}

/**
 * Convert select filter to simple object
 *
 * @param {HTMLElement} filter filter element
 * @returns {object} return value
 */
export function handleSelect(filter) {

  const returnValue = {};
  returnValue.multiple = [];
  window.multiselects[filter.id].getValue().forEach(value => {
    // Get multiselect option dataset
    const shownValue = window.multiselects[filter.id].options.find(v => v.value === value).shownValue;
    returnValue.multiple.push({
      name: filter.id,
      text: `
        ${filter.dataset.prefix ? filter.dataset.prefix + ' ' : ''}
        ${shownValue}
        ${filter.dataset.suffix ? filter.dataset.suffix + ' ' : ''}`.trim(),
      value: value
    })
  });

  return returnValue;
}

/**
 * Get selected filters from DOM and return
 * FilterTags -friendly object of selected values.
 *
 * @param {HTMLElement[]} inputs - Car search filters.
 * @returns {object[]}
 */
export function formatSelectedFiltersData( inputs ) {
  const returnValue = Array.from(inputs)
    .flatMap((filter) => {
      let returnValue = {};
      // If checkbox or radio, add this to returnValue

      if (isCheckboxOrRadioFilter(filter)) {
        returnValue = handleCheckboxAndRadio(filter);
      }

      // Text inputs, predictive etc.
      if (isTextInput(filter)) {
        returnValue = handleTextInput(filter);
      }

      // Get multiselects
      if (isSelectElement(filter)) {
        returnValue = handleSelect(filter);
      }

      if (Object.keys(returnValue).length && filter.dataset.noTag) {
        returnValue.noTag = true;
      }

      return returnValue.multiple ? returnValue.multiple : returnValue;
    })
    .filter((o) => Object.keys(o).length);

  return returnValue;
}
  
/**
 * Compare arrays for uniqueness. Both arrays contain the same values, order is irrelevant.
 * @param {string[]} a - array a
 * @param {string[]} b - array b
 * @returns {boolean} - true if both contain the same unique elements
 */
export function compareArrays(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;

  const setA = new Set(a);
  const setB = new Set(b);

  if (setA.size !== setB.size) return false;

  for (const val of setA) {
    if (!setB.has(val)) return false;
  }

  return true;
}
